import { NavLink } from 'react-router-dom'
import { LayoutDashboard, Search, CreditCard, Shield, Activity, Sparkles, ShieldCheck, Gift, X } from 'lucide-react'
import { useAuthStore } from '../../stores/authStore'
import { useAnalyzeStore } from '../../stores/analyzeStore'
import { APP_NAME } from '../../constants'

const navItems = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/analyze', label: 'Subscriptions', icon: Search },
  { to: '/rewards', label: 'Rewards', icon: Gift },
  { to: '/smartpay', label: 'Credit Cards', icon: CreditCard },
  { to: '/monitor', label: 'Monitor', icon: Shield },
  { to: '/activities', label: 'Activities', icon: Activity },
  { to: '/why-self-fi', label: `Why ${APP_NAME}`, icon: Sparkles },
]

interface SidebarProps {
  open?: boolean
  onClose?: () => void
}

export function Sidebar({ open, onClose }: SidebarProps) {
  const user = useAuthStore((s) => s.user)
  const reviewCount = useAnalyzeStore((s) => s.reviewCount)

  // Close mobile sidebar on navigation
  const handleNav = () => { onClose?.() }

  return (
    <>
      {/* Mobile overlay */}
      {open && (
        <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={onClose} />
      )}

      <aside className={`
        fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-slate-800 bg-slate-900
        transform transition-transform duration-200 ease-in-out
        lg:relative lg:translate-x-0
        ${open ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Logo */}
        <div className="flex h-16 items-center justify-between border-b border-slate-800 px-6">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-500/25">
              <svg viewBox="0 0 24 24" className="h-5 w-5 text-white" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            </div>
            <span className="text-xl font-bold bg-gradient-to-r from-indigo-400 to-violet-400 bg-clip-text text-transparent">{APP_NAME}</span>
          </div>
          <button onClick={onClose} className="lg:hidden text-slate-400 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={handleNav}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-indigo-600/20 text-indigo-400'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                }`
              }
            >
              <item.icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
              <span>{item.label}</span>
              {item.to === '/analyze' && reviewCount > 0 && (
                <span className="ml-auto rounded-full bg-amber-500/20 px-2 py-0.5 text-xs text-amber-400">{reviewCount}</span>
              )}
            </NavLink>
          ))}

          {/* Admin link (only for admins) */}
          {user?.is_admin && (
            <NavLink
              to="/admin"
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-indigo-600/20 text-indigo-400'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                }`
              }
            >
              <ShieldCheck className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
              <span>Admin</span>
            </NavLink>
          )}
        </nav>
      </aside>
    </>
  )
}
